import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BoomTestimonial2Name,
  BoomTestimonial8Name,
  BoomTestimonial9Name,
  BoomTestimonial10Name,
  PdpQuoteChris,
  PdpQuoteJanine,
  PdpQuoteKarin,
  PdpQuoteLisa,
  PdpTestimonialsTitle,
} from '../../../Constants';
import Preview8 from '../../../assets/8-preview.webp';
import { Icon } from '../../elements/icon';

/**
 * Testimonials for the product-suggestion page (PDP).
 *
 * Deliberately separate from BoomTestimonials: four fixed cards, no tag pills
 * and no scroll arrows. The quote sits on the media as a caption overlay, and
 * videos use a single play affordance instead of the native control bar.
 * BoomTestimonials stays in place everywhere else, including the home page.
 */
const PdpTestimonials = ({ titleClassName = 't-text-[24px] md:t-text-[30px] t-text-center' }) => {
  const { t } = useTranslation();

  const DATA = [
    {
      name: t(BoomTestimonial9Name),
      quote: t(PdpQuoteKarin),
      type: 'video',
      file: `${process.env.REACT_APP_PUBLIC_URL}resources/videos/Testimonials+/10.mp4`,
    },
    {
      name: t(BoomTestimonial8Name),
      quote: t(PdpQuoteLisa),
      type: 'video',
      file: `${process.env.REACT_APP_PUBLIC_URL}resources/videos/Testimonials+/8.mp4`,
      preview: Preview8,
    },
    {
      name: t(BoomTestimonial10Name),
      quote: t(PdpQuoteJanine),
      type: 'video',
      file: `${process.env.REACT_APP_PUBLIC_URL}resources/videos/Testimonials+/9.mp4`,
    },
    {
      name: t(BoomTestimonial2Name),
      quote: t(PdpQuoteChris),
      type: 'image',
      file: `${process.env.REACT_APP_PUBLIC_URL}resources/images/testimonials/2.webp`,
    },
  ];

  return (
    <div className="t-flex t-flex-col t-gap-4 t-mb-12 md:t-mb-16">
      <h2 className={`!t-m-0 t-font-serif t-font-normal t-text-navy ${titleClassName}`}>
        {t(PdpTestimonialsTitle)}
      </h2>

      {/* Scrolls horizontally on small screens, settles into four columns from md up. */}
      <div className="t-flex t-items-start t-gap-4 t-overflow-x-auto hide-scrollbar md:t-grid md:t-grid-cols-4 md:t-overflow-visible">
        {DATA.map((item, index) => (
          <Card {...item} key={'pdp-testimonial-' + index} />
        ))}
      </div>
    </div>
  );
};

const Card = ({ file, name, quote, type, preview }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const isVideo = type === 'video';

  const startPlayback = () => {
    const video = videoRef.current;
    if (!video) return;
    video.play();
    setPlaying(true);
  };

  return (
    <div className="t-flex-none md:t-flex-auto t-w-[220px] md:t-w-auto t-flex t-flex-col t-gap-2">
      <div className="t-relative t-w-full t-h-[320px] t-rounded-xl t-overflow-hidden t-border t-border-g200">
        {isVideo ? (
          <video
            ref={videoRef}
            className="t-w-full t-h-full t-object-cover"
            controls={playing}
            poster={preview}
            playsInline
            preload={preview ? 'none' : 'metadata'}
            onPause={() => setPlaying(false)}
            onEnded={() => setPlaying(false)}
          >
            {/* .MOV: omit type — declaring video/quicktime makes Chrome skip the source
                even though it can decode the file. Undeclared lets the browser sniff it. */}
            {/* No poster (e.g. Karin/Janine): append #t=0.1 so mobile Safari renders the
                first frame instead of a blank box. Cards with a poster are left untouched. */}
            <source src={preview ? file : `${file}#t=0.1`} type={/\.mov$/i.test(file) ? undefined : 'video/mp4'} />
          </video>
        ) : (
          <img src={file} alt="" className="t-w-full t-h-full t-object-cover" />
        )}

        {/* Caption overlay. Hidden while a video plays so it never covers the speaker. */}
        {!playing && (
          <>
            <div className="t-absolute t-bottom-0 t-left-0 t-right-0 t-h-2/3 t-bg-gradient-to-t t-from-black/90 t-via-black/50 t-to-transparent t-pointer-events-none" />

            {/* Click target sits under the caption so the whole thumbnail plays. */}
            {isVideo && (
              <button
                type="button"
                onClick={startPlayback}
                aria-label={`${name} – Video`}
                className="t-absolute t-inset-0 t-bg-transparent t-border-0 t-cursor-pointer"
              />
            )}

            <div className="t-absolute t-bottom-0 t-left-0 t-right-0 t-px-3 t-pb-3 t-flex t-flex-col t-gap-1.5 t-pointer-events-none">
              <p className="!t-m-0 t-font-sans t-text-[13px] t-leading-snug t-text-white">{quote}</p>
              <span className="t-font-sans t-text-[13px] t-font-semibold t-text-white">{name}</span>
              {isVideo && (
                <span className="t-flex t-items-center t-justify-center t-w-9 t-h-9 t-rounded-full t-bg-white/90 t-shadow-sm">
                  <Icon raw name="play" color="#103642" className="t-w-4 t-h-4 t-translate-x-[1px]" />
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PdpTestimonials;
